/**
 * Versioned HTTP contract — `finance-config-v1` — for the finance planning store (FINAPP-5, ADR 0084).
 *
 * The wire shapes `finance-api` RETURNS and the finance web console PARSES for the owner's
 * **planning config** — monthly budgets per category, category overrides/toggles, and forecast
 * settings. This is *user-authored* data held in Supabase (ADR 0084); the Google Sheet stays the
 * source of truth for ACTUALS (`finance-v1`). Both sides "parse, don't validate" against these
 * schemas without importing one another (ADR 0005). Reuses the shared `{data,error,meta}` envelope.
 *
 * Category `key` is stable and **aligns with a snapshot P&L line's `id`** (the slug of its label),
 * so the console can join a budget to its actual spend for drawdown. `monthlyBudget` is a
 * per-month figure in the snapshot's currency; `0` means "no budget set". `updatedAt` is epoch ms
 * stamped server-side by the injected clock on save — `0` is the sentinel for "never saved yet"
 * (a freshly-seeded default the owner hasn't persisted).
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';

/** Contract namespace (also the URL suffix `/api/finance/v1/config`). Envelope `meta.version` stays the shared `v1`. */
export const FINANCE_CONFIG_HTTP_CONTRACT_VERSION = 'finance-config-v1' as const;

const categoryKey = z.string().min(1).max(64);
const categoryLabel = z.string().min(1).max(120);
/** A per-month budget in the snapshot currency: finite, non-negative, sanely bounded. `0` = unset. */
const monthlyBudget = z.number().finite().nonnegative().max(1_000_000_000);

// ── Category planning row ────────────────────────────────────────────────────
export const FinanceCategoryConfigSchema = z.object({
  /** Stable key — aligns with the snapshot P&L line id (slug of the label), so a budget joins its actuals. */
  key: categoryKey,
  label: categoryLabel,
  /** Included in drawdown + the forecast when true; the on/off toggle. */
  active: z.boolean(),
  /** Per-month budget in the snapshot currency. `0` ⇒ no budget set. */
  monthlyBudget,
});
export type FinanceCategoryConfigV1 = z.infer<typeof FinanceCategoryConfigSchema>;

// ── Cash position (operator-stated real/company cash — a first actual in the DB, ADR 0084 §6) ──
/** Company cash on hand in the snapshot currency: finite, non-negative, sanely bounded. */
const cashAmount = z.number().finite().nonnegative().max(1_000_000_000);
const cashLabel = z.string().min(1).max(80);

/**
 * Stored shape: the stated cash `amount`, `asOf` (epoch ms — server-stamped when the amount changes,
 * so the figure carries an honest "as of" date), and an optional source `label` (e.g. "Bank — main").
 * When present the cash forecast projects from this instead of the card balance.
 */
export const FinanceCashPositionSchema = z.object({
  amount: cashAmount,
  asOf: z.number().int().nonnegative(),
  label: cashLabel.optional(),
});
export type FinanceCashPositionV1 = z.infer<typeof FinanceCashPositionSchema>;

/**
 * Input shape: the client sends `amount` (+ optional `label`); `asOf` is optional and acts as a
 * restamp sentinel — omitted or `0` tells the server to stamp it `now()` (the amount just changed),
 * a positive value is preserved (an unrelated save shouldn't move the "as of" date). No client clock.
 */
export const FinanceCashPositionInputSchema = z.object({
  amount: cashAmount,
  asOf: z.number().int().nonnegative().optional(),
  label: cashLabel.optional(),
});
export type FinanceCashPositionInputV1 = z.infer<typeof FinanceCashPositionInputSchema>;

// ── Forecast settings ────────────────────────────────────────────────────────
/** Project the cash forecast from the set budgets, or from each category's actual monthly average. */
export const FORECAST_MODES = ['budget', 'actual'] as const;
export const ForecastModeSchema = z.enum(FORECAST_MODES);
export type ForecastMode = z.infer<typeof ForecastModeSchema>;

/** Default months the runway forecast projects when a config is first seeded. */
export const DEFAULT_FORECAST_HORIZON_MONTHS = 12 as const;

export const FinanceForecastConfigSchema = z.object({
  mode: ForecastModeSchema,
  /** How many months forward the runway projection runs (capped). */
  horizonMonths: z.number().int().min(1).max(60),
});
export type FinanceForecastConfigV1 = z.infer<typeof FinanceForecastConfigSchema>;

// ── The owner's planning config (the GET payload / the persisted row) ────────
const categories = z
  .array(FinanceCategoryConfigSchema)
  .max(200)
  // Duplicate keys would make a budget→actual join ambiguous — reject at the boundary.
  .refine((rows) => new Set(rows.map((r) => r.key)).size === rows.length, {
    message: 'category keys must be unique',
  });

export const FinanceConfigSchema = z.object({
  categories,
  forecast: FinanceForecastConfigSchema,
  /** Operator-stated company cash the forecast projects from; absent ⇒ fall back to the card balance. */
  cashPosition: FinanceCashPositionSchema.optional(),
  /** Epoch ms of the last save (server-stamped). `0` ⇒ never saved (a seeded default). */
  updatedAt: z.number().int().nonnegative(),
});
export type FinanceConfigV1 = z.infer<typeof FinanceConfigSchema>;

export const FinanceConfigResponseSchema = apiEnvelope(FinanceConfigSchema);
export type FinanceConfigResponse = z.infer<typeof FinanceConfigResponseSchema>;

// ── PUT body — the client sends categories + forecast (+ optional cash position); the server
//    stamps `updatedAt` and the cash `asOf` ─
export const FinanceConfigInputSchema = z.object({
  categories,
  forecast: FinanceForecastConfigSchema,
  cashPosition: FinanceCashPositionInputSchema.optional(),
});
export type FinanceConfigInputV1 = z.infer<typeof FinanceConfigInputSchema>;

/**
 * The empty/default planning config — no categories, budget-mode forecast, never-saved (`updatedAt:0`).
 * Returned by `GET /v1/config` when the owner has no saved config; the console overlays it onto the
 * live snapshot categories (the source of truth for which categories exist) so the budget area is
 * never blank and always reflects new categories as they appear in the sheet.
 */
export function emptyFinanceConfig(): FinanceConfigV1 {
  return {
    categories: [],
    forecast: { mode: 'budget', horizonMonths: DEFAULT_FORECAST_HORIZON_MONTHS },
    updatedAt: 0,
  };
}
